import { watchOnce } from '@vueuse/core';
import { readonly, ref, watch } from 'vue';
/**
 * Never-read sentinel for `initialReadSeq`.
 *
 * Event seqs are 0-based (`getChatNextEventSeq` returns 0 for an empty chat), so 0 is a valid read
 * position and cannot double as "never read". Callers coerce a missing server read seq to this
 * value; it sits strictly below every real seq, so the initialize() anchor scan and the arrival
 * cursor both treat a seq-0 event as unread on first open.
 */
export const NEVER_READ_SEQ = -1;
/**
 * Owns the "New messages" divider anchor for a chat session.
 *
 * The anchor is deliberately NOT derived reactively from the read pointer. Deriving it makes the
 * divider pop in live while the user is watching (the read-seq snapshot taken at open lags the
 * incoming events), and anchoring on raw seqs lets events that render nothing for this viewer
 * produce a phantom divider. Instead the decision is made at *arrival time*: an anchorable event
 * from someone else anchors the divider only if it arrives while the user is not watching, and the
 * anchor then stays put (sticky for the session) until everything above it has been seen — only a
 * fresh unseen boundary can re-anchor it.
 *
 * Arrival detection is seq-monotonic (a cursor over event seqs, never array indices/lengths), so an
 * explicit reload that rebuilds the events array from scratch replays as a no-op.
 */
export function useUnreadDivider(params) {
    const dividerSeq = ref();
    // Captured once at open: events created at/after this are witnessed-live (see openedAt param).
    const openedAt = params.openedAt ?? Date.now();
    let initialized = false;
    /** Highest seq the viewer is known to have seen. Only ever increases. Seeded by initialize(). */
    let lastSeenSeq = NEVER_READ_SEQ;
    /** Arrival cursor: only events with a higher seq count as new. Never decreases. */
    let lastProcessedSeq = NEVER_READ_SEQ;
    // `loaded` and the read-seq lookup resolve asynchronously and in either order — initialize
    // exactly once when both are available. Try synchronously first (both may already be present),
    // otherwise watchOnce fires the single time the readiness condition flips to true and disposes
    // itself. `immediate` is deliberately omitted: with `once`, an immediate run against an unmet
    // condition would dispose the watcher before the data resolves.
    initialize();
    if (!initialized) {
        watchOnce(() => params.chat.loaded && params.initialReadSeq.value != null, initialize);
    }
    // Live arrivals. Deep watch so pushes into the reactive events array are observed; which events
    // are new is decided by the seq cursor, never by index/length deltas.
    watch(() => params.chat.events, processArrivals, { deep: true });
    return { dividerSeq: readonly(dividerSeq), onEventRead };
    function onEventRead(seq) {
        // Never clears dividerSeq — the divider is sticky for the session, matching the existing
        // UX where the indicator stays until the user leaves the chat.
        lastSeenSeq = Math.max(lastSeenSeq, seq);
    }
    function initialize() {
        if (initialized || !params.chat.loaded || params.initialReadSeq.value == null) {
            return;
        }
        initialized = true;
        const events = params.chat.events;
        // A viewer who authored the latest reply has necessarily seen everything before it —
        // preserves the existing "replying means you saw it" semantics.
        lastSeenSeq = params.initialReadSeq.value;
        let maxSeq = lastSeenSeq;
        for (const event of events) {
            if (params.isOwn(event)) {
                lastSeenSeq = Math.max(lastSeenSeq, event.seq);
            }
            maxSeq = Math.max(maxSeq, event.seq);
        }
        // Anchor above the first unseen event that actually renders for this viewer — trailing
        // invisible/transient events can never produce a phantom divider. Witnessed-live events are
        // skipped here: they happened while the viewer was present (e.g. the first agent message of
        // a chat the patient just started, landing in the pre-init window), so whether they anchor
        // is the watching-aware arrival path's call, not the isWatching-agnostic history scan's.
        for (const event of events) {
            if (!isWitnessedLive(event) && event.seq > lastSeenSeq && !params.isOwn(event) && params.canAnchor(event)) {
                dividerSeq.value = event.seq;
                break;
            }
        }
        lastProcessedSeq = maxSeq;
        // Replay the witnessed-live events present at open as arrivals so isWatching() decides each:
        // watched → seen (no divider); unseen (tab hidden while starting) → anchors, exactly as a
        // post-init arrival would. lastProcessedSeq already covers them, so processArrivals never
        // repeats this.
        for (const event of events) {
            if (isWitnessedLive(event)) {
                processArrival(event);
            }
        }
    }
    /**
     * True iff the viewer was present when this event happened, so it must go through the
     * watching-aware arrival path rather than the isWatching-agnostic history anchor scan. An event
     * qualifies if it arrived via the live subscription (`live`) OR was created at/after the viewer
     * opened the chat (`timestamp >= openedAt`). The OR covers both delivery transports and clock
     * skew: the first message of a just-started chat is caught whether it streams in live or lands
     * in the load fetch. A genuinely-unread history event (not live, created before open) fails
     * both and stays eligible to anchor.
     */
    function isWitnessedLive(event) {
        return event.live === true || event.timestamp >= openedAt;
    }
    function processArrivals() {
        if (!initialized) {
            return;
        }
        let cursor = lastProcessedSeq;
        for (const event of params.chat.events) {
            if (event.seq <= lastProcessedSeq) {
                continue;
            }
            processArrival(event);
            cursor = Math.max(cursor, event.seq);
        }
        lastProcessedSeq = cursor;
    }
    function processArrival(event) {
        if (params.isOwn(event)) {
            // Sending your own message means you're caught up.
            lastSeenSeq = Math.max(lastSeenSeq, event.seq);
            return;
        }
        if (!params.canAnchor(event)) {
            // Invisible/transient for this viewer — never affects the divider or the seen pointer.
            return;
        }
        if (params.isWatching()) {
            // The user sees it arrive — no divider should ever appear for it.
            lastSeenSeq = Math.max(lastSeenSeq, event.seq);
            return;
        }
        // Anchor only at a fresh boundary (no divider yet, or everything below the old one has
        // been seen). While unseen content remains above the anchor it stays put — the divider
        // never jumps under the user.
        if (dividerSeq.value == null || dividerSeq.value <= lastSeenSeq) {
            dividerSeq.value = event.seq;
        }
    }
}
//# sourceMappingURL=useUnreadDivider.js.map